import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db, auth } from "../../config";
import axios from "axios";
import { browserName } from "react-device-detect";
import apiClient from "../../utils/apiClient";
import Button from "../../components/Button/Button";
import "./Dashboard.css";
import Chart from "../../components/Chart";
import TransferMoneyModal from "../../components/TransferMoneyModal/TransferMoneyModal";
import AccountInfos from "../../components/AccountInfos/AccountInfos";
import Loader from "../../components/Loader";

const getApiError = (err) =>
  err.response?.data?.error || err.message || "Something went wrong";

const Dashboard = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [currentEmail, setCurrentEmail] = useState("");
  const [cashBalance, setCashBalance] = useState("");
  const [inboundPendingBalance, setInboundPendingBalance] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [setupIntentStatus, setSetupIntentStatus] = useState(false);
  const [ip, setIP] = useState("");
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [transfer, setTransfer] = useState(true);
  const [verifyBank, setVerifyBank] = useState(false);
  const [bankLinkStep, setBankLinkStep] = useState("link");
  const [withdraw, setWithdraw] = useState(true);
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [microAmount1, setMicroAmount1] = useState("");
  const [microAmount2, setMicroAmount2] = useState("");
  const [amount, setAmount] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadDashboard = async () => {
      const user = auth.currentUser;
      if (!user) return;

      setIsLoading(true);
      setLoadError("");

      try {
        await user.reload();

        if (!user.emailVerified) {
          if (!cancelled) {
            setLoadError(
              "Email verification required before using treasury features."
            );
            setIsLoading(false);
          }
          return;
        }

        await user.getIdToken(true);

        axios
          .get("https://geolocation-db.com/json/")
          .then((res) => setIP(res.data.ip))
          .catch(() => setIP("127.0.0.1"));

        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists() || !docSnap.data()?.accountId) {
          if (!cancelled) {
            setLoadError(
              "Your account setup is incomplete. Please sign out and create a new account."
            );
            setIsLoading(false);
          }
          return;
        }

        const data = docSnap.data();
        if (cancelled) return;

        setFirstName(data.firstName || "");
        setLastName(data.lastName || "");
        setCurrentEmail(data.email || "");
        if (data.setupIntentStatus) {
          setSetupIntentStatus(data.setupIntentStatus);
        }

        const [faRes, balanceRes] = await Promise.all([
          apiClient.post("Retrieve-financial-account", {}),
          apiClient.post("balance", {}),
        ]);

        if (cancelled) return;

        setCashBalance(faRes.data.FaCash / 100);
        setInboundPendingBalance(faRes.data.FaInboundPending / 100);
        setIsVerified(balanceRes.data.Verified);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setLoadError(getApiError(err));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, []);

  const toggleModal = () => {
    setModal((prevModal) => !prevModal);
    if (!modal) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
  };

  const AccountOnboard = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        await user.reload();
        if (!user.emailVerified) {
          setLoadError(
            "Email verification required before using treasury features."
          );
          return;
        }
        await user.getIdToken(true);
      }

      const { data } = await apiClient.post("Onboard-account", {});
      if (data.url) {
        window.location.replace(data.url);
      }
    } catch (err) {
      setLoadError(getApiError(err));
    }
  };

  const InboundTransfer = async (transferDescription, transferAmount) => {
    setProcessing(true);
    setError("");
    try {
      const { data } = await apiClient.post("inboundTransfer", {
        amount: transferAmount,
        description: transferDescription,
        currentEmail,
      });
      if (data.error) {
        setError(data.error);
        return;
      }
      setError("succeeded");
      setDescription("");
      setAmount("");
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setProcessing(false);
    }
  };

  const OutboundTransfer = async (transferDescription, transferAmount) => {
    setProcessing(true);
    setError("");
    try {
      const { data } = await apiClient.post("outboundTransfer", {
        amount: transferAmount,
        description: transferDescription,
        currentEmail,
      });
      if (data.error) {
        setError(data.error);
        return;
      }
      setError("succeeded");
      setDescription("");
      setAmount("");
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setProcessing(false);
    }
  };

  const VerifyBankAccount = async (routing, acctNumber) => {
    setProcessing(true);
    setError("");
    try {
      const { data } = await apiClient.post("verifyBank", {
        routingNumber: routing,
        accountNumber: acctNumber,
        ip,
        browserName,
        currentEmail,
        name: `${firstName} ${lastName}`,
        email: currentEmail,
      });
      if (data.error) {
        setError(data.error);
        return;
      }
      setSetupIntentStatus(data.setupIntentStatus);
      setRoutingNumber("");
      setAccountNumber("");

      if (data.requiresMicrodeposits) {
        setBankLinkStep("verify");
        setError(
          "Bank linked. Enter the two microdeposit amounts from your statement to finish."
        );
      } else if (data.setupIntentStatus === "succeeded") {
        setError("succeeded");
        toggleModal();
        setVerifyBank(false);
      } else {
        setError("succeeded");
      }
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setProcessing(false);
    }
  };

  const ConfirmMicrodeposits = async (deposit1, deposit2) => {
    setProcessing(true);
    setError("");
    try {
      const { data } = await apiClient.post("verify-microdeposits", {
        amounts: [Number(deposit1), Number(deposit2)],
      });
      if (data.error) {
        setError(data.error);
        return;
      }
      setSetupIntentStatus(data.setupIntentStatus);
      setMicroAmount1("");
      setMicroAmount2("");

      if (data.setupIntentStatus === "succeeded") {
        setError("succeeded");
        toggleModal();
        setVerifyBank(false);
        setBankLinkStep("link");
      } else {
        setError("Verification pending. Please try again shortly.");
      }
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setProcessing(false);
    }
  };

  const outBoundPayment = async (transferDescription, recipientEmail, transferAmount) => {
    setProcessing(true);
    setError("");

    try {
      const { data } = await apiClient.post("outbound-payment", {
        amount: transferAmount,
        description: transferDescription,
        email: recipientEmail,
        currentEmail,
      });

      if (data.error) {
        setError(data.error);
        return;
      }

      if (data.outboundPayment && data.expectedDate) {
        const createdDate = new Date(data.expectedDate * 1000);
        const expectedDate =
          createdDate.toLocaleDateString("en-US") +
          " " +
          createdDate.toLocaleTimeString("en-US");
        setError(`succeeded ,funds are expected to arrive on ${expectedDate}`);
        setEmail("");
        setDescription("");
        setAmount("");
      }
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="dashboard-page">
      <header className="dashboard-header">
        <div className="dashboard-header__intro">
          <p className="page-eyebrow">Overview</p>
          <h1 className="page-title">
            {isLoading ? "Loading…" : firstName ? `Welcome, ${firstName}` : "Your dashboard"}
          </h1>
        </div>
        <Link to="/changepassword" className="dashboard-link">
          Change password
        </Link>
      </header>

      {loadError && (
        <div className="dashboard-error-banner" role="alert">
          {loadError}
        </div>
      )}

      <div className="dashboard-grid">
        <section className="dashboard-chart-card">
          <div className="dashboard-card-header">
            <h2 className="dashboard-card-title">Transaction activity</h2>
            <span className="dashboard-card-subtitle">Your recent activity</span>
          </div>
          <div className="dashboard-chart-body">
            <Chart />
          </div>
        </section>

        <section className="dashboard-panel">
          <div className="dashboard-card-header">
            <h2 className="dashboard-card-title">Account</h2>
            <span className="dashboard-card-subtitle">Balances and account status</span>
          </div>

          <AccountInfos
            firstName={firstName}
            lastName={lastName}
            cashBalance={cashBalance}
            inboundPendingBalance={inboundPendingBalance}
            isVerified={isVerified}
            AccountOnboard={AccountOnboard}
            isLoading={isLoading}
          />

          {isLoading ? (
            <div className="verificationStatusLoaderDiv">
              <Loader size={50} />
            </div>
          ) : (
            <>
              {isVerified && setupIntentStatus !== "succeeded" && (
                <div className="createAndVerifyBankbtnDiv">
                  {setupIntentStatus === "requires_action" ? (
                    <Button
                      btnName="Verify microdeposits"
                      onClick={() => {
                        setError("");
                        setBankLinkStep("verify");
                        setVerifyBank(true);
                        setTransfer(false);
                        setWithdraw(false);
                        toggleModal();
                      }}
                      classe="btn--accent"
                    />
                  ) : (
                    <Button
                      btnName="Link bank account"
                      onClick={() => {
                        setError("");
                        setBankLinkStep("link");
                        setVerifyBank(true);
                        setTransfer(false);
                        setWithdraw(false);
                        toggleModal();
                      }}
                      classe="btn--accent"
                    />
                  )}
                </div>
              )}
            </>
          )}

          <div className="fundAndTransferDiv">
            {setupIntentStatus === "succeeded" && (
              <div className="btnTransferDiv">
                <Button
                  btnName="Add money"
                  onClick={() => {
                    setError("");
                    toggleModal();
                    setTransfer(true);
                    setVerifyBank(false);
                  }}
                  disabled={!isVerified}
                  classe="btn--accent"
                />
                <Button
                  btnName="Withdraw"
                  onClick={() => {
                    setError("");
                    toggleModal();
                    setWithdraw(true);
                    setTransfer(false);
                    setVerifyBank(false);
                  }}
                  disabled={!isVerified}
                  classe="btn--outline"
                />
                <Button
                  btnName="Send money"
                  onClick={() => {
                    setError("");
                    toggleModal();
                    setWithdraw(false);
                    setTransfer(false);
                    setVerifyBank(false);
                  }}
                  disabled={!isVerified}
                  classe="btn--outline"
                />
              </div>
            )}
          </div>
        </section>
      </div>

      {modal && (
        <TransferMoneyModal
          onClick={
            transfer
              ? InboundTransfer
              : withdraw
                ? OutboundTransfer
                : verifyBank
                  ? bankLinkStep === "verify"
                    ? ConfirmMicrodeposits
                    : VerifyBankAccount
                  : outBoundPayment
          }
          description={description}
          setDescription={setDescription}
          email={email}
          setEmail={setEmail}
          routingNumber={routingNumber}
          setRoutingNumber={setRoutingNumber}
          accountNumber={accountNumber}
          setAccountNumber={setAccountNumber}
          microAmount1={microAmount1}
          setMicroAmount1={setMicroAmount1}
          microAmount2={microAmount2}
          setMicroAmount2={setMicroAmount2}
          amount={amount}
          setAmount={setAmount}
          processing={processing}
          setProcessing={setProcessing}
          withdraw={withdraw}
          transfer={transfer}
          verifyBank={verifyBank}
          bankLinkStep={bankLinkStep}
          header={
            transfer
              ? "Add money"
              : withdraw
                ? "Withdraw"
                : verifyBank
                  ? bankLinkStep === "verify"
                    ? "Verify microdeposits"
                    : "Add bank account"
                  : "Send money"
          }
          setModal={setModal}
          setVerifyBank={setVerifyBank}
          error={
            error ? (
              <p
                className={
                  error.includes("succeeded")
                    ? "transfer-modal__message transfer-modal__message--success"
                    : "transfer-modal__message"
                }
              >
                {error}
              </p>
            ) : null
          }
        />
      )}
    </div>
  );
};

export default Dashboard;
